import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { Alert, Badge, Button, Dialog, EmptyState, Spinner, timeAgo } from '@shripos/ui';
import { api } from '../../lib/api.js';
import { localHolds, unsyncedHolds } from '../../lib/offline/holds.js';
import { rupees } from '../../lib/money.js';
import { isNetworkError, useOffline } from '../../stores/offline.js';
import { kitchenOf, useSession } from '../../stores/session.js';

/** A row of this device's copy, shaped like the server's list items. */
const fromLocal = (h) => ({
  id: h.id,
  clientUuid: h.clientUuid,
  tokenNo: h.tokenNo ?? null,
  tableNo: h.tableNo ?? null,
  orderType: h.orderType,
  customer: h.customer ?? null,
  itemCount: h.itemCount ?? h.lines.reduce((n, l) => n + l.qty, 0),
  grandTotalPaise: h.grandTotalPaise ?? h.totals?.grandTotalPaise ?? 0,
  createdAt: h.createdAt,
  createdBy: h.createdBy?.name ?? h.createdBy ?? null,
  selfOrder: h.selfOrder ?? null,
  local: Boolean(h.local),
});

/**
 * Open orders. Online: the server's list, plus orders held on this device offline that are still
 * syncing. Offline: this device's copy (refreshed while online), so any of them can be resumed.
 */
export function useHeldOrders() {
  return useQuery({
    queryKey: ['bills', 'held'],
    queryFn: async () => {
      try {
        const r = await api.get('/bills?status=held&limit=50');
        const waiting = await unsyncedHolds();
        const onServer = new Set(r.items.map((b) => b.clientUuid));
        const waitingIds = new Set(waiting.map((h) => h.clientUuid));
        const extra = waiting.filter((h) => !onServer.has(h.clientUuid)).map(fromLocal);
        return {
          items: [...extra, ...r.items.map((b) => ({ ...b, local: waitingIds.has(b.clientUuid) }))],
          total: r.total + extra.length,
          offline: false,
        };
      } catch (err) {
        if (!isNetworkError(err)) throw err;
        useOffline.getState().set({ reachable: false });
        const rows = (await localHolds()).map(fromLocal);
        return { items: rows, total: rows.length, offline: true };
      }
    },
    refetchInterval: 30_000,
    networkMode: 'always',
  });
}

export function HeldOrdersDialog({ onResume, onClose }) {
  const { t } = useTranslation();
  const held = useHeldOrders();
  const kitchenOrders = useSession((s) => kitchenOf(s.me).orders);
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pos.openOrders', 'Open orders')}
      description={
        kitchenOrders
          ? t('pos.openOrdersHint', 'Orders on hold or sent to the kitchen, not yet paid.')
          : t('pos.openOrdersHintNoKitchen', 'Orders on hold, not yet paid.')
      }
    >
      {held.data?.offline && (
        <Alert tone="warning" className="mb-3">
          {t(
            'pos.openOrdersOffline',
            'No internet: these are the open orders this device had when it was last online, plus the ones saved here since. Orders taken on other devices meanwhile are not shown.',
          )}
        </Alert>
      )}
      {held.isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : held.isError ? (
        <Alert tone="danger">{held.error.message}</Alert>
      ) : held.data.items.length === 0 ? (
        <EmptyState icon={Clock} title={t('pos.noOpenOrders', 'No open orders')} />
      ) : (
        <ul className="divide-y divide-slate-100">
          {held.data.items.map((b) => (
            <li key={b.clientUuid ?? b.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="font-semibold">
                  {b.tokenNo
                    ? `${t('pos.token', 'Token')} ${b.tokenNo}`
                    : kitchenOrders
                      ? t('pos.notSent', 'Not sent')
                      : t('pos.onHold', 'On hold')}
                  {b.tableNo ? ` · ${t('pos.table', 'Table')} ${b.tableNo}` : ''}
                  <span className="ml-2 text-sm font-normal text-slate-500 capitalize">
                    {t(`pos.type.${b.orderType}`, b.orderType.replace('_', '-'))}
                  </span>
                  {b.local && (
                    <Badge tone="warning" className="ml-2">
                      {t('pos.savedOnDevice', 'Saved on this device · syncing')}
                    </Badge>
                  )}
                  {b.selfOrder && (
                    <Badge tone={b.selfOrder.payWith === 'upi' ? 'warning' : 'info'} className="ml-2">
                      {b.selfOrder.payWith === 'upi'
                        ? t('pos.selfOrderCheckUpi', 'Self-order · check UPI payment')
                        : t('pos.selfOrderPayHere', 'Self-order · pay here')}
                    </Badge>
                  )}
                </p>
                <p className="truncate text-sm text-slate-500">
                  {t('pos.itemCount', '{{count}} item(s)', { count: b.itemCount })} · {rupees(b.grandTotalPaise)} ·{' '}
                  {timeAgo(b.createdAt)}
                  {b.customer?.name ? ` · ${b.customer.name}` : ''}
                  {b.selfOrder?.customerName ? ` · ${b.selfOrder.customerName}` : ''}
                  {b.createdBy ? ` · ${b.createdBy}` : ''}
                </p>
              </div>
              <Button size="sm" onClick={() => onResume(b)}>
                {t('pos.open', 'Open')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
